/**
 * Shapes shared by the SharePoint and OneDrive file routes of `cti-sharepoint-mcp`, which answer the
 * same snake_case item and version wire for both sources.
 */

/** A file's metadata as the viewer needs it. `etag` changes whenever the file's bytes change. */
export interface DriveFileItem {
  itemId: string;
  name: string;
  contentType: string | null;
  size: number;
  etag: string | null;
  webUrl: string | null;
  lastModifiedAt: string | null;
}

/** One stored version of a file, newest first in a list. */
export interface DriveFileVersion {
  versionId: string;
  lastModifiedAt: string | null;
  /** Who saved this version, when the source says. */
  lastModifiedBy: string | null;
  size: number | null;
  isCurrent: boolean;
}

export interface DriveFileItemWire {
  item_id?: string;
  name?: string;
  content_type?: string | null;
  size?: number;
  etag?: string | null;
  web_url?: string | null;
  last_modified_at?: string | null;
}

interface DriveFileVersionWire {
  version_id?: string;
  last_modified_at?: string | null;
  last_modified_by?: { name?: string | null } | null;
  size?: number | null;
  is_current?: boolean;
}

export function driveFileItemFromWire(wire: DriveFileItemWire): DriveFileItem {
  return {
    itemId: wire.item_id ?? '',
    name: wire.name ?? '',
    contentType: wire.content_type ?? null,
    size: typeof wire.size === 'number' ? wire.size : -1,
    etag: wire.etag ?? null,
    webUrl: wire.web_url ?? null,
    lastModifiedAt: wire.last_modified_at ?? null,
  };
}

/**
 * A file's versions as far as the source listed them. `truncated` is true when the source has older
 * versions it did not list; a server that does not say counts as a complete list.
 */
export interface DriveFileVersionList {
  versions: DriveFileVersion[];
  truncated: boolean;
}

/** The version list off the wire. A row without an id addresses nothing and is dropped. */
export function driveFileVersionsFromWire(body: {
  versions?: DriveFileVersionWire[];
  truncated?: boolean;
}): DriveFileVersionList {
  const versions = (body.versions ?? [])
    .filter((wire) => typeof wire.version_id === 'string' && wire.version_id)
    .map((wire) => ({
      versionId: wire.version_id as string,
      lastModifiedAt: wire.last_modified_at ?? null,
      lastModifiedBy: wire.last_modified_by?.name ?? null,
      size: typeof wire.size === 'number' ? wire.size : null,
      isCurrent: wire.is_current === true,
    }));
  return { versions, truncated: body.truncated === true };
}

/** Raised by a version restore; 409 means the file changed since it was read. */
export class DriveFileRestoreError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'DriveFileRestoreError';
  }
}

/** The server's own sentence from a failed call, in either error shape these routes answer. */
export async function driveErrorMessage(
  response: Response,
  fallback: string,
): Promise<string> {
  const body = (await response.json().catch(() => null)) as {
    message?: unknown;
    error?: { message?: unknown };
  } | null;
  const message = body?.error?.message ?? body?.message;
  if (typeof message === 'string' && message.trim()) return message;
  return `${fallback} (${response.status})`;
}
